import { ErrorMessage, Field, Form, Formik } from "formik";
import React, { BaseSyntheticEvent, useEffect, useRef, useState } from "react";
import { supportSchema } from "./validation/support";
import { useTranslation } from "react-i18next";
import { useSupportMutation } from "../../graphql/support.hooks";
import { CustomToast } from "../../components/inputs/toast";
import support_img from "./img/support.svg";
import { ReactComponent as UploadIcon } from "./img/upload.svg";
import { SUPPORT_SUBJECTS } from "../../constants";
import { Spinner } from "react-bootstrap";
import { FileEarmarkArrowDown, XCircle } from "react-bootstrap-icons";
import { simulateClickOnSupportChat } from "../../utils";

export const Support = () => {
  const { t } = useTranslation();
  const formikRef = useRef(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const textareaRef = useRef(null);
  const [value, setValue] = useState("");

  const [getSupport, { loading: supportLoading }] = useSupportMutation();
  const [showToast, setShowToast] = useState<boolean>(false);
  const [encodingFile, setEncodingFile] = useState(null);
  const [fileError, setFileError] = useState<string>("");
  const [file, setFile] = useState<File | null>(null);

  useEffect(() => {
    document.title = "Support";
  }, []);

  const initialValues = {
    subject: "",
    message: "",
  };

  const clearFileInput = () => {
    setFile(null);
    setEncodingFile("");
    setFileError("");
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const clickOnFileInput = () => {
    if (fileInputRef.current !== null) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event: BaseSyntheticEvent) => {
    const file = event.target.files[0];
    const maxSizeInBytes = 20 * 1024 * 1024;
    if (file) setFile(file);

    if (file && file.size > maxSizeInBytes) {
      setFileError(t("support.largeFile"));
    } else if (file && file !== null) {
      setFileError("");
      const reader = new FileReader();
      reader.onloadend = () => {
        setEncodingFile((reader.result as string).split(",")[1]);
      };
      reader.readAsDataURL(file);
    }
  };

  const trySendSupport = async (values: any) => {
    if (fileError) return;

    const { data } = await getSupport({
      variables: encodingFile
        ? { ...values, file: encodingFile }
        : { ...values },
    });
    if (data.support && formikRef.current) {
      formikRef.current.resetForm();
      clearFileInput();
      setShowToast(true);
    }
  };

  const handleChange = (e: BaseSyntheticEvent) => {
    if (!textareaRef?.current) return;
    const textarea = textareaRef.current;
    setValue(e.target.value);

    textarea.style.height = "auto";
    textarea.style.height = textarea.scrollHeight + "px";
  };

  return (
    <div className="support-container container position-relative">
      <div className="d-flex justify-content-between align-items-center">
        <div className="mb-4 mb-md-5">
          <h1 className="d-none d-md-block">{t("menu.support")}</h1>
          <h2 className="d-block d-md-none">{t("menu.support")}</h2>
          <p className="text-gray fs-6 mb-0">
            {t("support.Contact ME-Page’s team anytime if you need help")}
          </p>
        </div>
      </div>
      <div className="support-content d-flex flex-column flex-xl-row justify-content-center align-items-center bg-white rounded">
        <Formik
          innerRef={formikRef}
          initialValues={initialValues}
          validationSchema={supportSchema(t)}
          onSubmit={trySendSupport}
          enableReinitialize
        >
          {({ handleSubmit, touched, errors, values, setFieldValue }) => (
            <Form className="d-flex flex-column align-items-center col-12 col-md-11 col-xl-6">
              <h4 className="d-none d-md-block col-12 text-left text-dark fw-semibold">
                {t("support.Email Request Form")}
              </h4>
              <h5 className="d-block d-md-none col-12 text-left text-dark fw-semibold">
                {t("support.Email Request Form")}
              </h5>
              <p className="column-text col-12 text-left text-gray fs-6 mb-4">
                {t("support.Please select subject and describe your problem")}
              </p>
              <div className="col-12 form-floating mb-4 position-relative d-flex flex-column align-items-center">
                <Field
                  as="select"
                  name="subject"
                  className={`form-select text-gray d-flex align-items-center ps-3 fs-6 text-black ${
                    touched.subject && errors.subject ? "is-invalid" : ""
                  }`}
                >
                  <option value="" disabled>
                    {t("support.Select Subject")}
                  </option>
                  {SUPPORT_SUBJECTS.map((subject, index) => (
                    <option key={index} value={subject}>
                      {subject}
                    </option>
                  ))}
                </Field>
                <label className="text-gray ps-3">{t("support.Subject")}</label>
                <ErrorMessage
                  name="subject"
                  component="div"
                  className="invalid-feedback d-block"
                />
              </div>
              <div className="col-12 form-floating">
                <Field
                  as="textarea"
                  innerRef={textareaRef}
                  value={value}
                  name="message"
                  className={`form-control mb-1 ${touched.message && errors.message ? "is-invalid" : ""}`}
                  placeholder={t(
                    "support.Describe your problem in few senteces"
                  )}
                  style={{
                    minHeight: "100px",
                    resize: "none",
                    overflow: "hidden",
                  }}
                  onChange={(e: BaseSyntheticEvent) => {
                    handleChange(e);
                    setFieldValue("message", e.target.value);
                  }}
                />
                <label className="text-gray fs-6" htmlFor="message">
                  {t("support.Describe your problem in few senteces")}
                </label>
                <ErrorMessage
                  name="message"
                  component="div"
                  className="invalid-feedback"
                />
                <div className="d-flex justify-content-between text-gray fs-7 mb-2 mb-md-3">
                  <p className="col-7 col-md-auto">
                    {t("support.Description must be more than 50 characters")}
                  </p>
                  <p>{values?.message?.length || 0} / 5000</p>
                </div>
              </div>
              <div className="col-12 mb-4 mb-md-5">
                {file === null ? (
                  <button
                    onClick={(e: BaseSyntheticEvent) => {
                      e.preventDefault();
                      clickOnFileInput();
                    }}
                    className={`col-12 py-3 mb-1 btn btn-outline-primary bg-primary-light primary-hover d-flex align-items-center justify-content-center`}
                  >
                    <UploadIcon />
                    <p className="ms-2 fw-normal mb-0 fs-6">
                      {t("support.Upload Your File")}
                    </p>
                  </button>
                ) : (
                  <div className="d-flex align-items-center justify-content-between col-12 px-3 py-2 rounded-1 mb-1 bg-primary-light border border-primary">
                    <div className="d-flex align-items-center">
                      <FileEarmarkArrowDown
                        className="fs-6 me-2"
                        color="#3e4957"
                        size={27}
                      />
                      <div className="d-flex flex-column">
                        <p className="mb-0 text-gray fs-6">{file.name}</p>
                        <p className="mb-0 text-gray fs-7">
                          {formatBytes(file.size)}
                        </p>
                      </div>
                    </div>
                    <XCircle
                      onClick={clearFileInput}
                      className="fs-5 cursor-pointer"
                      color="#3e4957"
                      size={21}
                    />
                  </div>
                )}
                <p
                  className={`${fileError ? "text-danger" : "text-gray"} fs-7 mb-2 mb-md-3`}
                >
                  {t("support.Maximum file size is 20 MB")}
                </p>
                <input
                  ref={fileInputRef}
                  className="d-none"
                  type="file"
                  onChange={(e: BaseSyntheticEvent) => handleFileChange(e)}
                />
              </div>
              <button
                disabled={supportLoading}
                onClick={() => handleSubmit()}
                className="col-12 col-md-7 col-xl-6 py-3 btn btn-primary fw-bold text-white fs-6"
              >
                {t("support.Send Request")}
                {supportLoading && (
                  <Spinner animation="border" role="status" className="ms-2">
                    <span className="visually-hidden">
                      {t("profile.loading")}...
                    </span>
                  </Spinner>
                )}
              </button>
            </Form>
          )}
        </Formik>
        <div className="border-line"></div>
        <div className="col-12 col-md-11 col-xl-3 d-flex flex-column align-items-center justify-content-between align-self-stretch">
          <div>
            <h4 className="d-none d-md-block col-12 text-left text-dark fw-semibold">
              {t("support.Online Chat")}
            </h4>
            <h5 className="d-block d-md-none col-12 text-left text-dark fw-semibold">
              {t("support.Online Chat")}
            </h5>
            <p className="column-text col-12 text-left text-gray mb-0">
              {t(
                "support.Contact with our customer support online to receive your problem"
              )}
            </p>
          </div>
          <img className="m-5 m-xl-0" src={support_img} alt="support picture" />
          <button
            onClick={simulateClickOnSupportChat}
            className="col-12 col-md-7 col-xl-12 py-3 btn btn-secondary text-white fw-bold fs-6"
          >
            {t("support.Start Online Chat")}
          </button>
        </div>
        <CustomToast
          isShow={showToast}
          setShow={setShowToast}
          message={t("support.success")}
          isSeenClose={false}
          background="bg-success-light"
          text="text-success fw-400 fs-6"
          check={true}
        />
      </div>
    </div>
  );
};
